import { createOpenAI } from '@ai-sdk/openai';
import { google } from '@ai-sdk/google';
import { createAnthropic } from '@ai-sdk/anthropic';
import { generateText, streamText, tool } from 'ai';
import type { VexalynConfig } from '@vexalyn/config';
import type { AIProvider, Message, ProviderName, ProviderTool, ToolStreamEvent } from '@vexalyn/shared';
import type { ZodTypeAny } from 'zod';

function extractJson(text: string): string | null {
  const start = text.indexOf('{');
  if (start === -1) return null;

  let depth = 0;
  let inString = false;
  let escaped = false;
  for (let index = start; index < text.length; index++) {
    const character = text[index];
    if (inString) {
      if (escaped) escaped = false;
      else if (character === '\\') escaped = true;
      else if (character === '"') inString = false;
      continue;
    }
    if (character === '"') inString = true;
    else if (character === '{') depth++;
    else if (character === '}' && --depth === 0) return text.substring(start, index + 1);
  }
  return null;
}

function extractDsmlToolCalls(text: string, availableNames: readonly string[]): { tool: string; input: Record<string, unknown> }[] {
  if (!text.includes('<｜DSML｜tool_calls>')) return [];
  const calls: { tool: string; input: Record<string, unknown> }[] = [];
  const invokePattern = /<｜DSML｜invoke\s+name="([^"]+)"\s*>([\s\S]*?)<\/｜DSML｜invoke>/g;
  let invokeMatch: RegExpExecArray | null;

  while ((invokeMatch = invokePattern.exec(text)) !== null) {
    const tool = normalizeToolName(invokeMatch[1] ?? '', availableNames);
    const body = invokeMatch[2] ?? '';
    const input: Record<string, unknown> = {};
    const parameterPattern = /<｜DSML｜parameter\s+name="([^"]+)"(?:\s+string="true")?\s*>([\s\S]*?)<\/｜DSML｜parameter>/g;
    let parameterMatch: RegExpExecArray | null;

    while ((parameterMatch = parameterPattern.exec(body)) !== null) {
      const name = parameterMatch[1];
      const value = parameterMatch[2]?.trim() ?? '';
      if (name) input[name] = value;
    }

    if (tool) calls.push({ tool, input });
  }

  return calls;
}

export function normalizeToolName(name: string, availableNames: readonly string[] = []): string {
  const aliases: Record<string, string> = {
    createdirectory: 'create_directory',
    deletedirectory: 'delete_directory',
    getprojectstructure: 'get_project_structure',
    readfile: 'read_file',
    writefile: 'write_file',
    editfile: 'edit_file',
    deletefile: 'delete_file',
    searchfiles: 'search_files',
    runcommand: 'run_command',
    websearch: 'web_search',
    fetchwebpage: 'fetch_web_page',
    semanticsearch: 'semantic_search',
    getcodeoutline: 'get_code_outline',
    gitstatus: 'git_status',
    gitdiff: 'git_diff',
  };
  const compactName = name.toLowerCase().replace(/[\s_-]/g, '');
  const alias = aliases[compactName];
  if (alias) return alias;
  return availableNames.find(candidate => candidate.toLowerCase().replace(/[\s_-]/g, '') === compactName) ?? name;
}

// ─── SdkProvider: wraps Vercel AI SDK streamText ──────────────────────────────
class SdkProvider implements AIProvider {
  constructor(
    public readonly name: ProviderName,
    public readonly model: string,
    private readonly languageModel: Parameters<typeof streamText>[0]['model'],
  ) {}

  async *stream(messages: readonly Message[]): AsyncIterable<string> {
    let streamError: unknown;
    const result = streamText({
      model: this.languageModel,
      messages: [...messages],
      onError: ({ error }) => { streamError = error; },
    });
    let receivedText = false;
    for await (const part of result.fullStream) {
      if (part.type === 'error') { streamError = part.error; continue; }
      if (part.type !== 'text-delta') continue;
      receivedText ||= part.textDelta.trim().length > 0;
      yield part.textDelta;
    }
    if (streamError !== undefined) throw toProviderError(streamError);
    if (!receivedText) throw new Error(`${this.name} returned an empty response for model ${this.model}`);
  }

  async *streamWithTools(messages: readonly Message[], tools: readonly ProviderTool[]): AsyncIterable<ToolStreamEvent> {
    const sdkTools = Object.fromEntries(tools.map((t) => [
      t.name,
      tool({
        description: t.description,
        parameters: t.inputSchema as ZodTypeAny,
        execute: async (input: unknown) => t.execute(input),
      }),
    ]));
    const result = streamText({
      model: this.languageModel,
      messages: [...messages],
      tools: sdkTools,
      maxSteps: 10,
    });
    for await (const part of result.fullStream) {
      if (part.type === 'text-delta') yield { type: 'text-delta', text: part.textDelta };
      if (part.type === 'tool-call')   yield { type: 'tool-call',   name: part.toolName,  input: part.args };
      if (part.type === 'tool-result') yield { type: 'tool-result', name: part.toolName,  result: part.result };
      if (part.type === 'error') throw toProviderError(part.error);
    }
  }
}

// ─── PromptToolProvider: emulates tool calls for models without native support ─
class PromptToolProvider implements AIProvider {
  constructor(
    public readonly name: ProviderName,
    public readonly model: string,
    private readonly base: SdkProvider,
  ) {}

  stream(messages: readonly Message[]): AsyncIterable<string> {
    return this.base.stream(messages);
  }

  async *streamWithTools(messages: readonly Message[], tools: readonly ProviderTool[]): AsyncIterable<ToolStreamEvent> {
    const toolDocs = tools.map(t => {
      let params = '';
      try {
        const schema = t.inputSchema as { shape?: Record<string, unknown> };
        if (schema?.shape) params = Object.keys(schema.shape).join(', ');
      } catch {}
      return `- ${t.name}(${params}): ${t.description}`;
    }).join('\n');

    const toolInstruction = `\n\nTOOL CALLING PROTOCOL:\nYou have access to these tools:\n${toolDocs}\n\nTo call a tool, respond with ONLY valid JSON:\n{"tool":"<name>","input":{<params>}}\n\nCall one tool at a time. After all tools are done, respond normally with plain text.\nNEVER write tool calls as markdown or YAML.`;

    const augmented: Message[] = [...messages];
    const sysIdx = augmented.findIndex(m => m.role === 'system');
    if (sysIdx >= 0) {
      augmented[sysIdx] = { ...augmented[sysIdx]!, content: augmented[sysIdx]!.content + toolInstruction };
    } else {
      augmented.unshift({ role: 'system', content: toolInstruction });
    }

    const history: Message[] = [...augmented];
    let steps = 0;

    while (steps < 10) {
      steps++;
      let fullText = '';
      try {
        const result = await generateText({
          model: (this.base as unknown as { languageModel: Parameters<typeof generateText>[0]['model'] }).languageModel,
          messages: history,
        });
        fullText = result.text;
      } catch {
        for await (const chunk of this.base.stream(history)) fullText += chunk;
      }

      const jsonStr = extractJson(fullText);
      if (jsonStr) {
        let parsed: { tool?: string; input?: unknown } = {};
        try { parsed = JSON.parse(jsonStr); } catch {}
        const normalizedToolName = parsed.tool
          ? normalizeToolName(parsed.tool, tools.map(toolDef => toolDef.name))
          : '';
        if (normalizedToolName && tools.find(t => t.name === normalizedToolName)) {
          const toolDef = tools.find(t => t.name === normalizedToolName)!;
          let toolInput = parsed.input ?? (parsed as { arguments?: unknown }).arguments;
          if (!toolInput || typeof toolInput !== 'object') {
            const rest = { ...parsed };
            delete rest.tool;
            delete rest.input;
            delete (rest as { arguments?: unknown }).arguments;
            toolInput = rest;
          }
          yield { type: 'tool-call', name: normalizedToolName, input: toolInput ?? {} };
          history.push({ role: 'assistant', content: fullText });
          let toolResult: { ok: boolean; value?: unknown; error?: string };
          try {
            const raw = await toolDef.execute(toolInput ?? {});
            toolResult = raw as { ok: boolean; value?: unknown };
          } catch (err) {
            toolResult = { ok: false, error: err instanceof Error ? err.message : String(err) };
          }
          yield { type: 'tool-result' as const, name: normalizedToolName, result: toolResult };
          history.push({ role: 'user', content: `Tool result for ${normalizedToolName}: ${JSON.stringify(toolResult)}. Continue.` });
          continue;
        }
      }

      const dsmlCalls = extractDsmlToolCalls(fullText, tools.map(toolDef => toolDef.name));
      if (dsmlCalls.length > 0) {
        history.push({ role: 'assistant', content: fullText });
        const results: { tool: string; result: { ok: boolean; value?: unknown; error?: string } }[] = [];
        for (const call of dsmlCalls) {
          const toolDef = tools.find(t => t.name === call.tool);
          if (!toolDef) continue;
          yield { type: 'tool-call', name: call.tool, input: call.input };
          let toolResult: { ok: boolean; value?: unknown; error?: string };
          try {
            const raw = await toolDef.execute(call.input);
            toolResult = raw as { ok: boolean; value?: unknown };
          } catch (err) {
            toolResult = { ok: false, error: err instanceof Error ? err.message : String(err) };
          }
          yield { type: 'tool-result' as const, name: call.tool, result: toolResult };
          results.push({ tool: call.tool, result: toolResult });
        }
        history.push({ role: 'user', content: `Tool results: ${JSON.stringify(results)}. Continue.` });
        continue;
      }

      for (const char of fullText) yield { type: 'text-delta', text: char };
      break;
    }
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function toProviderError(error: unknown): Error {
  if (error instanceof Error) return error;
  if (typeof error === 'string') return new Error(error);
  try { return new Error(JSON.stringify(error)); } catch { return new Error('Provider returned an unreadable error'); }
}

function requireKey(value: string | undefined, variable: string): string {
  if (!value || value.startsWith('PASTE_')) {
    throw new Error(`${variable} is required. Please set it in your .env file.`);
  }
  return value;
}

/** Create an OpenAI-compatible provider */
function openAiCompat(name: ProviderName, model: string, apiKey: string, baseURL: string): AIProvider {
  const client = createOpenAI({ apiKey, baseURL });
  const base = new SdkProvider(name, model, client(model));
  return new PromptToolProvider(name, model, base);
}

function promptToolProvider(
  name: ProviderName,
  model: string,
  languageModel: Parameters<typeof streamText>[0]['model'],
): AIProvider {
  return new PromptToolProvider(name, model, new SdkProvider(name, model, languageModel));
}

// ─── Provider factories ───────────────────────────────────────────────────────
export function createOpenAiProvider(config: VexalynConfig): AIProvider {
  const client = createOpenAI({
    apiKey: requireKey(config.openAiApiKey, 'OPENAI_API_KEY'),
    baseURL: 'https://api.openai.com/v1',
  });
  return promptToolProvider('openai', config.defaultModel, client(config.defaultModel));
}

export function createGeminiProvider(config: VexalynConfig): AIProvider {
  requireKey(config.googleGenerativeAiApiKey, 'GOOGLE_GENERATIVE_AI_API_KEY');
  return promptToolProvider('gemini', config.defaultModel, google(config.defaultModel));
}

export function createOpenRouterProvider(config: VexalynConfig): AIProvider {
  return openAiCompat('openrouter', config.defaultModel,
    requireKey(config.openRouterApiKey, 'OPENROUTER_API_KEY'),
    'https://openrouter.ai/api/v1');
}

export function createGroqProvider(config: VexalynConfig): AIProvider {
  return openAiCompat('groq', config.defaultModel,
    requireKey(config.groqApiKey, 'GROQ_API_KEY'),
    'https://api.groq.com/openai/v1');
}

export function createOllamaProvider(config: VexalynConfig): AIProvider {
  return openAiCompat('ollama', config.defaultModel, 'ollama',
    `${config.ollamaBaseUrl.replace(/\/$/, '')}/v1`);
}

export function createQwenProvider(config: VexalynConfig): AIProvider {
  return openAiCompat('qwen', config.defaultModel,
    requireKey(config.qwenApiKey, 'QWEN_API_KEY'),
    'https://dashscope-intl.aliyuncs.com/compatible-mode/v1');
}

export function createDeepSeekProvider(config: VexalynConfig): AIProvider {
  return openAiCompat('deepseek', config.defaultModel,
    requireKey(config.deepseekApiKey, 'DEEPSEEK_API_KEY'),
    config.deepseekBaseUrl);
}

export function createZaiProvider(config: VexalynConfig): AIProvider {
  return openAiCompat('zai', config.defaultModel,
    requireKey(config.zaiApiKey, 'ZAI_API_KEY'),
    config.zaiBaseUrl);
}

export function createMoonshotProvider(config: VexalynConfig): AIProvider {
  return openAiCompat('moonshot', config.defaultModel,
    requireKey(config.moonshotApiKey, 'MOONSHOT_API_KEY'),
    config.moonshotBaseUrl);
}

export function createAnthropicProvider(config: VexalynConfig): AIProvider {
  const anthropic = createAnthropic({
    apiKey: requireKey(config.anthropicApiKey, 'ANTHROPIC_API_KEY'),
    baseURL: config.anthropicBaseUrl,
  });
  return promptToolProvider(
    'anthropic',
    config.defaultModel,
    anthropic(config.defaultModel) as unknown as Parameters<typeof streamText>[0]['model'],
  );
}

export function createMistralProvider(config: VexalynConfig): AIProvider {
  return openAiCompat('mistral', config.defaultModel,
    requireKey(config.mistralApiKey, 'MISTRAL_API_KEY'),
    config.mistralBaseUrl);
}

export function createXaiProvider(config: VexalynConfig): AIProvider {
  return openAiCompat('xai', config.defaultModel,
    requireKey(config.xaiApiKey, 'XAI_API_KEY'),
    config.xaiBaseUrl);
}

export function createCerebrasProvider(config: VexalynConfig): AIProvider {
  return openAiCompat('cerebras', config.defaultModel,
    requireKey(config.cerebrasApiKey, 'CEREBRAS_API_KEY'),
    config.cerebrasBaseUrl);
}

export function createTogetherProvider(config: VexalynConfig): AIProvider {
  return openAiCompat('together', config.defaultModel,
    requireKey(config.togetherApiKey, 'TOGETHER_API_KEY'),
    config.togetherBaseUrl);
}

export function createFireworksProvider(config: VexalynConfig): AIProvider {
  return openAiCompat('fireworks', config.defaultModel,
    requireKey(config.fireworksApiKey, 'FIREWORKS_API_KEY'),
    config.fireworksBaseUrl);
}

export function createDeepinfraProvider(config: VexalynConfig): AIProvider {
  return openAiCompat('deepinfra', config.defaultModel,
    requireKey(config.deepinfraApiKey, 'DEEPINFRA_API_KEY'),
    config.deepinfraBaseUrl);
}

export function createSiliconflowProvider(config: VexalynConfig): AIProvider {
  return openAiCompat('siliconflow', config.defaultModel,
    requireKey(config.siliconflowApiKey, 'SILICONFLOW_API_KEY'),
    config.siliconflowBaseUrl);
}

export function createNvidiaNimProvider(config: VexalynConfig): AIProvider {
  return openAiCompat('nvidia', config.defaultModel,
    requireKey(config.nvidiaNimApiKey, 'NVIDIA_NIM_API_KEY'),
    config.nvidiaNimBaseUrl);
}

export function createCohereProvider(config: VexalynConfig): AIProvider {
  return openAiCompat('cohere', config.defaultModel,
    requireKey(config.cohereApiKey, 'COHERE_API_KEY'),
    config.cohereBaseUrl);
}

export function createPerplexityProvider(config: VexalynConfig): AIProvider {
  return openAiCompat('perplexity', config.defaultModel,
    requireKey(config.perplexityApiKey, 'PERPLEXITY_API_KEY'),
    config.perplexityBaseUrl);
}

export function createSambanovaProvider(config: VexalynConfig): AIProvider {
  return openAiCompat('sambanova', config.defaultModel,
    requireKey(config.sambanovaApiKey, 'SAMBANOVA_API_KEY'),
    config.sambanovaBaseUrl);
}

export function createNovitaProvider(config: VexalynConfig): AIProvider {
  return openAiCompat('novita', config.defaultModel,
    requireKey(config.novitaApiKey, 'NOVITA_API_KEY'),
    config.novitaBaseUrl);
}

export function createAi21Provider(config: VexalynConfig): AIProvider {
  return openAiCompat('ai21', config.defaultModel,
    requireKey(config.ai21ApiKey, 'AI21_API_KEY'),
    config.ai21BaseUrl);
}

// ─── Main dispatch ────────────────────────────────────────────────────────────
export function createProvider(config: VexalynConfig): AIProvider {
  switch (config.defaultProvider) {
    case 'openai':      return createOpenAiProvider(config);
    case 'gemini':      return createGeminiProvider(config);
    case 'openrouter':  return createOpenRouterProvider(config);
    case 'groq':        return createGroqProvider(config);
    case 'ollama':      return createOllamaProvider(config);
    case 'qwen':        return createQwenProvider(config);
    case 'deepseek':    return createDeepSeekProvider(config);
    case 'zai':         return createZaiProvider(config);
    case 'moonshot':    return createMoonshotProvider(config);
    case 'anthropic':   return createAnthropicProvider(config);
    case 'mistral':     return createMistralProvider(config);
    case 'xai':         return createXaiProvider(config);
    case 'cerebras':    return createCerebrasProvider(config);
    case 'together':    return createTogetherProvider(config);
    case 'fireworks':   return createFireworksProvider(config);
    case 'deepinfra':   return createDeepinfraProvider(config);
    case 'siliconflow': return createSiliconflowProvider(config);
    case 'nvidia':      return createNvidiaNimProvider(config);
    case 'cohere':      return createCohereProvider(config);
    case 'perplexity':  return createPerplexityProvider(config);
    case 'sambanova':   return createSambanovaProvider(config);
    case 'novita':      return createNovitaProvider(config);
    case 'ai21':        return createAi21Provider(config);
  }
}

// ─── Mock provider (for tests) ────────────────────────────────────────────────
export class MockAIProvider implements AIProvider {
  readonly name = 'openai' as const;
  constructor(public readonly model = 'mock-model', private readonly response = 'Mock response') {}
  async *stream(messages: readonly Message[]): AsyncIterable<string> {
    void messages;
    for (const token of this.response.split(' ')) yield `${token} `;
  }
}
